/**
 * PresentationMode — optional AI-narrated, self-guided sales presentation.
 *
 * Pick a profile (Executive 2m / Operator 5m / Full 10m); a professional AI voice
 * (OpenAI TTS via backend) narrates each step while the UI drives the admin
 * navigation. Auto-advances when narration finishes (unless paused). Controls:
 * Play/Pause, Replay, Prev, Skip, Voice on/off, and playback speed. Demo-gated.
 */
import React, { useEffect, useRef, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiError } from "../../lib/api";
import { toast } from "sonner";
import {
  Play, Pause, RotateCcw, SkipForward, SkipBack, Volume2, VolumeX, X,
  Gauge, Presentation, Clock, Copy, Loader2, CheckCircle2, CalendarCheck, LayoutDashboard,
} from "lucide-react";

const SPEEDS = [1, 1.25, 1.5];

export const PresentationMode = ({ open, setTab, onClose, initialProfileId, onPlayingChange }) => {
  const navigate = useNavigate();
  const [profiles, setProfiles] = useState([]);
  const [voices, setVoices] = useState([]);
  const [voiceId, setVoiceId] = useState(() => localStorage.getItem("demoNarrationVoice") || "uk_female");
  const [profile, setProfile] = useState(null);       // {id,label,steps:[...]}
  const [i, setI] = useState(0);
  const [playing, setPlaying] = useState(false);
  const [finished, setFinished] = useState(false);
  const [voiceOn, setVoiceOn] = useState(true);
  const [speed, setSpeed] = useState(1);
  const [loadingAudio, setLoadingAudio] = useState(false);
  const audioRef = useRef(null);
  const audioCache = useRef({});       // step index -> base64
  const advanceTimer = useRef(null);

  const steps = profile?.steps || [];
  const step = steps[i];
  const isLast = i === steps.length - 1;

  // Load profile catalogue on open.
  useEffect(() => {
    if (!open) return;
    const stored = localStorage.getItem("demoNarrationVoice");
    if (stored) setVoiceId(stored);   // pick up a choice made in the welcome modal
    api.get("/admin/demo/presentation/profiles")
      .then(({ data }) => { setProfiles(data.profiles || []); setVoices(data.voices || []); })
      .catch((e) => toast.error(formatApiError(e)));
  }, [open]);

  // Changing the narration voice invalidates any cached audio so the new voice
  // takes effect immediately (persist the choice so it applies on every launch).
  const changeVoice = useCallback((id) => {
    setVoiceId(id);
    try { localStorage.setItem("demoNarrationVoice", id); } catch (_) { /* ignore */ }
    audioCache.current = {};
    if (audioRef.current) { try { audioRef.current.pause(); } catch (_) { /* ignore */ } }
  }, []);

  const clearTimer = () => { if (advanceTimer.current) { clearTimeout(advanceTimer.current); advanceTimer.current = null; } };

  const reset = useCallback(() => {
    clearTimer();
    if (audioRef.current) { audioRef.current.pause(); }
    audioCache.current = {};
    setProfile(null); setI(0); setPlaying(false); setFinished(false); setLoadingAudio(false);
  }, []);

  // Report active playback upward (used by Kiosk Mode to avoid interrupting a
  // running presentation with the idle timer).
  useEffect(() => { onPlayingChange?.(open && playing); }, [open, playing, onPlayingChange]);

  const close = useCallback(() => { reset(); onClose(); }, [reset, onClose]);

  const startProfile = useCallback(async (pid) => {
    try {
      const { data } = await api.get(`/admin/demo/presentation/${pid}`);
      audioCache.current = {};
      setProfile(data); setI(0); setFinished(false); setPlaying(true);
    } catch (e) { toast.error(formatApiError(e)); }
  }, []);

  // When launched with a pre-chosen profile (e.g. from the welcome selector),
  // skip the picker and start it directly.
  useEffect(() => {
    if (open && initialProfileId && !profile) startProfile(initialProfileId);
  }, [open, initialProfileId, profile, startProfile]);

  const next = useCallback(() => { clearTimer(); setI((n) => Math.min(n + 1, steps.length - 1)); }, [steps.length]);
  const prev = useCallback(() => { clearTimer(); setI((n) => Math.max(n - 1, 0)); }, []);

  // Drive admin tab when the step changes.
  useEffect(() => {
    if (open && step?.tab) setTab(step.tab);
  }, [open, i, step, setTab]);

  // Play narration (or run a read-timer) whenever the step changes while playing.
  useEffect(() => {
    if (!open || !profile || !step) return;
    clearTimer();

    if (!playing) { if (audioRef.current) audioRef.current.pause(); return; }

    let cancelled = false;
    const runVoice = async () => {
      try {
        let b64 = audioCache.current[i];
        if (!b64) {
          setLoadingAudio(true);
          const { data } = await api.post("/admin/demo/presentation/narrate", { text: step.narration, voice: voiceId });
          b64 = data.audio_base64;
          audioCache.current[i] = b64;
        }
        if (cancelled) return;
        setLoadingAudio(false);
        const el = audioRef.current;
        if (el) {
          el.src = `data:audio/mp3;base64,${b64}`;
          el.playbackRate = speed;
          await el.play().catch(() => {});
        }
      } catch (e) {
        setLoadingAudio(false);
        toast.error(`Narration failed: ${formatApiError(e)}`);
        // Fall back to a read-timer so the show goes on.
        runTimer();
      }
    };
    const runTimer = () => {
      const words = (step.narration || "").split(/\s+/).length;
      const ms = Math.max(4500, Math.round((words / speed) * 360));
      advanceTimer.current = setTimeout(() => { if (!cancelled) handleEnded(); }, ms);
    };

    if (voiceOn) runVoice(); else runTimer();
    return () => { cancelled = true; clearTimer(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, profile, i, playing, voiceOn, speed, voiceId]);

  const handleEnded = useCallback(() => {
    if (isLast) { setPlaying(false); setFinished(true); return; }
    setI((n) => Math.min(n + 1, steps.length - 1));
  }, [isLast, steps.length]);

  const togglePlay = () => {
    setPlaying((p) => {
      const np = !p;
      if (!np && audioRef.current) audioRef.current.pause();
      if (!np) clearTimer();
      return np;
    });
  };

  const replay = () => {
    clearTimer();
    const el = audioRef.current;
    if (voiceOn && el && audioCache.current[i]) { el.currentTime = 0; el.play().catch(() => {}); setPlaying(true); }
    else { setPlaying(true); setI((n) => n); }
  };

  const cycleSpeed = () => {
    const nextSpeed = SPEEDS[(SPEEDS.indexOf(speed) + 1) % SPEEDS.length];
    setSpeed(nextSpeed);
    if (audioRef.current) audioRef.current.playbackRate = nextSpeed;
  };

  const copy = (text) => {
    try { navigator.clipboard?.writeText(text)?.catch(() => {}); } catch (_) { /* unavailable */ }
    toast.success("Copied");
  };

  if (!open) return null;

  // ── Profile picker ───────────────────────────────────────────────────
  if (!profile) {
    return (
      <div className="fixed inset-0 z-[85] bg-black/60 backdrop-blur-sm grid place-items-center p-4" data-testid="presentation-picker">
        <div className="bg-card border border-border rounded-xl shadow-2xl max-w-lg w-full p-6">
          <div className="flex items-start justify-between">
            <div className="flex items-center gap-2.5">
              <div className="w-9 h-9 grid place-items-center bg-[#6D28D9] text-white rounded-md"><Presentation className="w-5 h-5" /></div>
              <div>
                <h3 className="font-heading text-lg font-black tracking-tight">AI Presentation Mode</h3>
                <p className="text-xs text-muted-foreground">Choose a self-guided, narrated walkthrough.</p>
              </div>
            </div>
            <button onClick={close} className="text-muted-foreground hover:text-foreground" data-testid="presentation-picker-close"><X className="w-5 h-5" /></button>
          </div>
          <div className="mt-4 space-y-2.5">
            {profiles.map((p) => (
              <button
                key={p.id}
                onClick={() => startProfile(p.id)}
                data-testid={`presentation-profile-${p.id}`}
                className="w-full text-left p-3.5 border border-border rounded-lg hover:border-[#6D28D9] hover:bg-[#6D28D9]/[0.04] transition-colors"
              >
                <div className="flex items-center justify-between">
                  <span className="font-semibold">{p.label}</span>
                  <span className="text-xs text-muted-foreground flex items-center gap-1"><Clock className="w-3 h-3" />{p.duration_label}</span>
                </div>
                <p className="text-xs text-muted-foreground mt-0.5">{p.subtitle} · {p.step_count} steps</p>
              </button>
            ))}
          </div>
          {voices.length > 0 && (
            <div className="mt-4">
              <label htmlFor="narration-voice" className="label-mini text-muted-foreground flex items-center gap-1.5 mb-1.5">
                <Volume2 className="w-3.5 h-3.5" /> Narration voice
              </label>
              <select
                id="narration-voice"
                value={voiceId}
                onChange={(e) => changeVoice(e.target.value)}
                data-testid="presentation-voice-select"
                className="w-full p-2.5 text-sm rounded-lg border border-border bg-background focus:border-[#6D28D9] focus:outline-none"
              >
                {voices.map((v) => (
                  <option key={v.id} value={v.id}>{v.label}{v.default ? " (recommended)" : ""}</option>
                ))}
              </select>
            </div>
          )}
          <p className="text-[11px] text-muted-foreground mt-4">A professional AI voice narrates each step and the screen follows along automatically. You can pause or take control at any time.</p>
        </div>
      </div>
    );
  }

  // ── Closing CTA (presentation finished) ──────────────────────────────
  if (finished) {
    return (
      <div className="fixed inset-0 z-[85] bg-[#0b1f3a]/85 backdrop-blur-sm grid place-items-center p-4" data-testid="presentation-finished">
        <div className="bg-card border border-border rounded-2xl shadow-2xl max-w-lg w-full overflow-hidden">
          <div className="bg-[#0b1f3a] text-white p-6 text-center">
            <CheckCircle2 className="w-12 h-12 text-[#5e9bff] mx-auto" />
            <h2 className="font-heading text-2xl font-black mt-3">Thank you for exploring Crystal Dispatch</h2>
            <p className="text-white/75 text-sm mt-2 max-w-md mx-auto leading-relaxed">
              You've just experienced a demonstration of a modern, AI-powered taxi dispatch platform
              designed to improve operational efficiency, compliance and customer service.
            </p>
          </div>
          <div className="p-4 space-y-2.5">
            <button
              onClick={() => navigate("/request-demo")}
              data-testid="finished-request-demo"
              className="w-full p-3.5 rounded-xl bg-[#6D28D9] text-white font-semibold flex items-center justify-center gap-2 hover:bg-[#5b21b6] transition-colors"
            >
              <CalendarCheck className="w-4 h-4" /> Request a Live Demonstration
            </button>
            <button
              onClick={close}
              data-testid="finished-explore"
              className="w-full p-3.5 rounded-xl border border-border font-semibold flex items-center justify-center gap-2 hover:border-[#6D28D9] transition-colors"
            >
              <LayoutDashboard className="w-4 h-4" /> Explore the Dashboard
            </button>
            <button
              onClick={() => { setFinished(false); setI(0); setPlaying(true); }}
              data-testid="finished-replay"
              className="w-full p-3.5 rounded-xl border border-dashed border-border font-semibold flex items-center justify-center gap-2 hover:border-foreground/30 transition-colors"
            >
              <RotateCcw className="w-4 h-4" /> Replay Presentation
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ── Player ───────────────────────────────────────────────────────────
  const pct = Math.round(((i + 1) / steps.length) * 100);
  return (
    <div className="fixed inset-x-0 bottom-0 z-[85] p-3 sm:p-5 pointer-events-none" data-testid="presentation-player">
      <audio ref={audioRef} onEnded={handleEnded} className="hidden" />
      <div className="max-w-2xl mx-auto bg-card border border-border rounded-xl shadow-2xl pointer-events-auto overflow-hidden">
        <div className="h-1 bg-muted"><div className="h-full bg-[#6D28D9] transition-all" style={{ width: `${pct}%` }} /></div>
        <div className="p-4 sm:p-5">
          <div className="flex items-start justify-between gap-3">
            <div>
              <div className="label-mini text-muted-foreground flex items-center gap-2">
                <span className="text-[#6D28D9] font-bold">{profile.label}</span> · <span data-testid="presentation-step-counter">Step {i + 1} of {steps.length}</span>
                {loadingAudio && <Loader2 className="w-3 h-3 animate-spin" />}
              </div>
              <h3 className="font-heading text-lg font-black tracking-tight leading-tight">{step.title}</h3>
            </div>
            <button onClick={close} className="text-muted-foreground hover:text-foreground" data-testid="presentation-close"><X className="w-5 h-5" /></button>
          </div>

          <p className="mt-2.5 text-sm text-foreground/80 leading-relaxed" data-testid="presentation-narration">{step.narration}</p>

          {step.credentials && (
            <div className="mt-3 p-3 rounded-md bg-muted/50 border border-border" data-testid="presentation-credentials">
              <div className="label-mini text-muted-foreground mb-1.5">{step.credentials.label}</div>
              <div className="flex items-center justify-between gap-2">
                <code className="font-mono-tab text-sm truncate">{step.credentials.email}</code>
                <button onClick={() => copy(step.credentials.email)} className="text-muted-foreground hover:text-foreground flex-shrink-0" title="Copy email"><Copy className="w-3.5 h-3.5" /></button>
              </div>
              <div className="text-xs text-muted-foreground mt-1">Password: <code className="font-mono-tab">demo123</code> · open in a private/incognito window to keep the {step.credentials.role} session separate.</div>
            </div>
          )}

          <div className="mt-4 flex items-center justify-between gap-2 flex-wrap">
            <div className="flex items-center gap-1.5">
              <button onClick={prev} disabled={i === 0} className="p-2 rounded-md border border-border disabled:opacity-40" title="Previous" data-testid="presentation-prev"><SkipBack className="w-4 h-4" /></button>
              <button onClick={togglePlay} className="p-2.5 rounded-md bg-[#6D28D9] text-white" title={playing ? "Pause" : "Play"} data-testid="presentation-playpause">
                {playing ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
              </button>
              <button onClick={replay} className="p-2 rounded-md border border-border" title="Replay step" data-testid="presentation-replay"><RotateCcw className="w-4 h-4" /></button>
              <button onClick={next} disabled={isLast} className="p-2 rounded-md border border-border disabled:opacity-40" title="Skip" data-testid="presentation-skip"><SkipForward className="w-4 h-4" /></button>
            </div>
            <div className="flex items-center gap-1.5">
              <button onClick={() => setVoiceOn((v) => !v)} className={`p-2 rounded-md border flex items-center gap-1 text-xs ${voiceOn ? "border-[#6D28D9] text-[#6D28D9]" : "border-border text-muted-foreground"}`} title="Voice on/off" data-testid="presentation-voice-toggle">
                {voiceOn ? <Volume2 className="w-4 h-4" /> : <VolumeX className="w-4 h-4" />}
              </button>
              <button onClick={cycleSpeed} className="px-2.5 py-2 rounded-md border border-border text-xs flex items-center gap-1" title="Playback speed" data-testid="presentation-speed">
                <Gauge className="w-3.5 h-3.5" /> {speed}x
              </button>
              <button onClick={close} className="px-2.5 py-2 rounded-md border border-border text-xs" data-testid="presentation-exit">Exit</button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PresentationMode;
